import { PrivateRoutes } from '@/constants/routes'
import { Tabs } from '@/components/ui/Tabs'
import React, { useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import ListCard from './components/ListCard/ListCard'
import TablePrincipal from './components/TablePrincipal/TablePrincipal'
import TableCustody from './components/TableCustody/TableCustody'
import TableRetirados from './components/TableRetirados/TableRetirados'
import TableRecibidos from './components/TableRecibidos/TableRecibidos'
import { useSelector } from 'react-redux'
import { motion } from 'framer-motion'
import { IConfigSeller } from '@/models/configSeller'
import { loadMacstoreChat } from '@/utils/loadMacstoreChat'
import { STATUS_GROUPS, TradeinKpiKey } from '@/utils/tradeinStatusGroups'
import { webApiService } from '@/services'
import { MdHome, MdInventory2, MdOutlineSwapHoriz, MdOutlineAccessTime } from 'react-icons/md'
import HomePageLegacy from './legacy/HomePageLegacy'
import { useHasRedesign } from '@/utils/redesignSellers'

const HomePageRedesign: React.FC = () => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const userState = useSelector((store: any) => store.user)
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const isSamsungPeru = configSeller.config.s3.FOLDER_APP === 'ti_samsung_pe'
  const isHuaweiOnlineChile = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_cl'
  const isHuaweiOnlineMexico = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_mx'
  const isHuaweiOnlinePeru = configSeller.config.s3.FOLDER_APP === 'ti_huawei_online_pe'
  const isChatEnabled = configSeller.config.permisos.ENABLE_CHATBOT

  const { slug } = useParams()
  const [selectedKpi, setSelectedKpi] = useState<TradeinKpiKey>('cotizados')
  const showInsights = [Number(import.meta.env.VITE_PERFIL_SOLOLECTURA), Number(import.meta.env.VITE_PERFIL_ADMIN), Number(import.meta.env.VITE_PERFIL_TI_REGION), Number(import.meta.env.VITE_PERFIL_JEFETIENDA), Number(import.meta.env.VITE_TI_JEFETIENDA_SELLOS), Number(import.meta.env.VITE_PERFIL_EJECUTIVO), Number(import.meta.env.VITE_PERFIL_EJECUTIVO_CUSTODIA), Number(import.meta.env.VITE_PERFIL_EJECUTIVO_CAJA), Number(import.meta.env.VITE_PERFIL_ADMIN_SELLER)].includes(userState.id_role)
  const homeRoute = PrivateRoutes.HOME.replace(':slug', String(slug))

  const handleSelectKpi = (kpi: TradeinKpiKey): void => {
    setSelectedKpi(kpi)
    if (pathname !== homeRoute) navigate(homeRoute)
  }

  const showRecibidosTab = ![Number(import.meta.env.VITE_PERFIL_TI_COTIZADOR)].includes(userState.id_role) && !isSamsungPeru && !isHuaweiOnlineChile && !isHuaweiOnlineMexico && !isHuaweiOnlinePeru
  const showCustodiaRetiradosTabs = [Number(import.meta.env.VITE_PERFIL_SOLOLECTURA), Number(import.meta.env.VITE_PERFIL_ADMIN), Number(import.meta.env.VITE_PERFIL_TI_REGION), Number(import.meta.env.VITE_PERFIL_JEFETIENDA), Number(import.meta.env.VITE_TI_JEFETIENDA_SELLOS)].includes(userState.id_role) && !isSamsungPeru && !isHuaweiOnlineChile && !isHuaweiOnlineMexico && !isHuaweiOnlinePeru

  const [statusCounts, setStatusCounts] = useState<Array<{ id_status: number, cantidad: number }>>([])
  useEffect(() => {
    webApiService.getCountStatus(userState.email, String(slug)).then((res) => {
      setStatusCounts(res.data ?? [])
    }).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])
  const countFor = (ids: number[]): number => statusCounts
    .filter((row) => ids.includes(row.id_status))
    .reduce((acc, row) => acc + Number(row.cantidad ?? 0), 0)
  const retiradoIdStatus = (isSamsungPeru || isHuaweiOnlineChile || isHuaweiOnlineMexico) ? 11 : 7
  // Total real de la vista "Principal": cotizados + en evaluación + finalizados. Deja afuera
  // estados no clasificados (ej. Caducado) que nunca se muestran bajo este tab.
  const totalCount = countFor([...STATUS_GROUPS.cotizados, ...STATUS_GROUPS.evaluacion, ...STATUS_GROUPS.finalizados])

  const tabItems = [
    { key: homeRoute, label: 'Principal', count: totalCount, icon: <MdHome /> },
    ...(showRecibidosTab ? [{ key: PrivateRoutes.RECIBIDOS.replace(':slug', String(slug)), label: 'Equipos recibidos', count: countFor([5]), icon: <MdOutlineSwapHoriz /> }] : []),
    ...(showCustodiaRetiradosTabs ? [{ key: PrivateRoutes.ENCUSTODIA.replace(':slug', String(slug)), label: 'Equipos custodia', count: countFor([9]), icon: <MdInventory2 /> }] : []),
    ...(showCustodiaRetiradosTabs ? [{ key: PrivateRoutes.RETIRADOS.replace(':slug', String(slug)), label: 'Equipos retirados', count: countFor([retiradoIdStatus]), icon: <MdOutlineAccessTime /> }] : [])
  ]

  useEffect(() => {
    const cleanup = isChatEnabled ? loadMacstoreChat(configSeller.config.s3.FOLDER_APP) : undefined
    return () => {
      cleanup?.()
      document.getElementById('webchat-container')?.remove()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isChatEnabled])

  return (
    <motion.div
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -10, opacity: 0 }}
      transition={{ duration: 0.3 }} className='space-y-4'
    >
      {showInsights ? <ListCard selectedKpi={selectedKpi} onSelectKpi={handleSelectKpi} /> : null}
      <div className='bg-white rounded-2xl shadow-md border border-slate-200 p-2 mt-4'>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <Tabs
            value={pathname}
            onChange={(key) => navigate(key)}
            items={tabItems}
            className={[Number(import.meta.env.VITE_PERFIL_CUSTODIA)].includes(userState.id_role) ? '!hidden' : ''}
          />
          <div id='table-toolbar' className='flex items-center gap-2 flex-wrap' />
        </div>
        <div className='mt-4'>
          <Routes>
            <Route path='/' element={[Number(import.meta.env.VITE_PERFIL_CUSTODIA)].includes(userState.id_role) ? <TableRecibidos /> : <TablePrincipal statusIds={STATUS_GROUPS[selectedKpi]} selectedKpi={selectedKpi} />} />
            <Route path='/recibidos' element={<TableRecibidos />} />
            <Route path='/custodia' element={<TableCustody />} />
            <Route path='/retirados' element={<TableRetirados />} />
            <Route path='/*' element={<Navigate replace to={PrivateRoutes.HOME} />} />
          </Routes>
        </div>
      </div>
    </motion.div>
  )
}

// El Home con KPIs, tabs nuevos y tablas rediseñadas solo se muestra a los sellers liberados
// (ver REDESIGN_SELLERS); el resto sigue con el Home anterior.
const HomePage: React.FC = () => (useHasRedesign() ? <HomePageRedesign /> : <HomePageLegacy />)

export default HomePage
